import { useState, useEffect, useCallback } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Loader2, Plus, Truck, Pencil, Phone, Mail } from "lucide-react";

const EMPTY = { name: "", contact_person: "", phone: "", email: "", org_nr: "", supplies: "", notes: "" };

export default function SuppliersPanel() {
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialog, setDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [showInactive, setShowInactive] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try { const res = await api.get("/admin/suppliers"); setSuppliers(res.data.suppliers || []); }
    catch { toast.error("Kunde inte ladda leverantörer"); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const openNew = () => { setEditing(null); setForm(EMPTY); setDialog(true); };
  const openEdit = (s) => {
    setEditing(s);
    setForm({ name: s.name || "", contact_person: s.contact_person || "", phone: s.phone || "",
              email: s.email || "", org_nr: s.org_nr || "", supplies: s.supplies || "", notes: s.notes || "" });
    setDialog(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      if (editing) await api.put(`/admin/suppliers/${editing.id}`, form);
      else await api.post("/admin/suppliers", form);
      toast.success(editing ? "Leverantör uppdaterad" : "Leverantör tillagd");
      setDialog(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Kunde inte spara"); }
    finally { setSaving(false); }
  };

  const toggleActive = async (s) => {
    try { await api.put(`/admin/suppliers/${s.id}`, { active: !s.active }); load(); }
    catch { toast.error("Misslyckades"); }
  };

  const shown = suppliers.filter(s => showInactive || s.active !== false);

  return (
    <div className="max-w-3xl" data-testid="suppliers-panel">
      <div className="flex items-center justify-between mb-5 gap-3 flex-wrap">
        <div>
          <h2 className="font-playfair text-xl font-bold text-white">Leverantörer</h2>
          <p className="text-[#C8C8CE] text-xs mt-1">Vilka ni köper från — kontakt, org.nr och vad de levererar.</p>
        </div>
        <div className="flex gap-2 items-center">
          <button onClick={() => setShowInactive(v => !v)} className="text-xs text-[#C0C0C6] hover:text-white">
            {showInactive ? "Dölj arkiverade" : "Visa arkiverade"}
          </button>
          <Button onClick={openNew} className="bg-[#D4B483] text-[#0A0A0A] hover:bg-[#E5CFA5] font-semibold">
            <Plus className="w-4 h-4 mr-2" /> Ny leverantör
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 text-[#D4B483] animate-spin" /></div>
      ) : shown.length === 0 ? (
        <div className="bg-[#121212] border border-[#27272A] rounded-xl p-10 text-center">
          <Truck className="w-8 h-8 text-[#3F3F46] mx-auto mb-3" />
          <p className="text-[#C0C0C6] text-sm">Inga leverantörer ännu.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {shown.map(s => (
            <div key={s.id} className={`bg-[#121212] rounded-xl border border-[#27272A] p-4 flex items-start gap-3 ${s.active === false ? "opacity-50" : ""}`}>
              <div className="w-8 h-8 rounded-full bg-[#D4B483]/10 flex items-center justify-center shrink-0">
                <Truck className="w-4 h-4 text-[#D4B483]" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm text-white font-medium">
                  {s.name}
                  {s.active === false && <span className="ml-2 text-[10px] px-2 py-0.5 rounded-full bg-[#6B7280]/20 text-[#A8ACB4]">Arkiverad</span>}
                </p>
                <p className="text-[11px] text-[#9A9AA2] mt-0.5">
                  {[s.contact_person, s.org_nr && `org.nr ${s.org_nr}`].filter(Boolean).join(" · ")}
                </p>
                <p className="text-[11px] text-[#C0C0C6] mt-0.5 flex items-center gap-3 flex-wrap">
                  {s.phone && <span className="inline-flex items-center gap-1"><Phone className="w-3 h-3" />{s.phone}</span>}
                  {s.email && <span className="inline-flex items-center gap-1"><Mail className="w-3 h-3" />{s.email}</span>}
                </p>
                {s.supplies && <p className="text-[11px] text-[#C8C8CE] mt-1">Levererar: {s.supplies}</p>}
              </div>
              <div className="flex gap-1.5 shrink-0">
                <button onClick={() => openEdit(s)} className="p-1.5 text-[#C0C0C6] hover:text-[#D4B483]" title="Redigera">
                  <Pencil className="w-3.5 h-3.5" />
                </button>
                <button onClick={() => toggleActive(s)} className="text-[11px] text-[#9A9AA2] hover:text-white">
                  {s.active === false ? "Återaktivera" : "Arkivera"}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={dialog} onOpenChange={setDialog}>
        <DialogContent className="bg-[#121212] border-[#27272A] max-w-md">
          <DialogHeader><DialogTitle className="text-white">{editing ? "Redigera leverantör" : "Ny leverantör"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <Label className="text-[#E8E8EA] text-xs mb-1.5 block">Namn *</Label>
                <Input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                  className="bg-[#0A0A0A] border-[#27272A] text-[#EDEDED]" />
              </div>
              <div>
                <Label className="text-[#E8E8EA] text-xs mb-1.5 block">Kontaktperson</Label>
                <Input value={form.contact_person} onChange={e => setForm(f => ({ ...f, contact_person: e.target.value }))}
                  className="bg-[#0A0A0A] border-[#27272A] text-[#EDEDED]" />
              </div>
              <div>
                <Label className="text-[#E8E8EA] text-xs mb-1.5 block">Org.nr</Label>
                <Input value={form.org_nr} onChange={e => setForm(f => ({ ...f, org_nr: e.target.value }))}
                  className="bg-[#0A0A0A] border-[#27272A] text-[#EDEDED]" />
              </div>
              <div>
                <Label className="text-[#E8E8EA] text-xs mb-1.5 block">Telefon</Label>
                <Input value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))}
                  className="bg-[#0A0A0A] border-[#27272A] text-[#EDEDED]" />
              </div>
              <div>
                <Label className="text-[#E8E8EA] text-xs mb-1.5 block">E-post</Label>
                <Input value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
                  className="bg-[#0A0A0A] border-[#27272A] text-[#EDEDED]" />
              </div>
            </div>
            <div>
              <Label className="text-[#E8E8EA] text-xs mb-1.5 block">Vad levererar de? (t.ex. kött, bröd, dryck)</Label>
              <Input value={form.supplies} onChange={e => setForm(f => ({ ...f, supplies: e.target.value }))}
                className="bg-[#0A0A0A] border-[#27272A] text-[#EDEDED]" />
            </div>
            <div>
              <Label className="text-[#E8E8EA] text-xs mb-1.5 block">Anteckningar</Label>
              <Textarea value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} rows={2}
                className="bg-[#0A0A0A] border-[#27272A] text-[#EDEDED] resize-none" />
            </div>
            <Button onClick={save} disabled={saving || !form.name.trim()}
              className="w-full bg-[#D4B483] text-[#0A0A0A] hover:bg-[#E5CFA5] font-semibold">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null} Spara
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
